/**
 * Which company you are in, and how to leave it for another.
 *
 * Lives in the sidebar header, next to the Railit mark — the mark says which
 * product this is, the chip beside it says which of your companies you are
 * looking at. Those two facts belong together, and putting the switcher up in
 * the breadcrumb left the sidebar naming a tenant it could not change.
 *
 * One Railit account can belong to any number of companies with a different role
 * in each, so this is not decoration: it is the only way to move between them.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Check, ChevronsUpDown, Plus, Settings as SettingsIcon } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/shadcn/dropdown-menu';
import { Skeleton } from '@/components/shadcn/skeleton';
import { NewOrgDialog } from '@/components/layout/NewOrgDialog';
import { useI18n } from '@/contexts/I18nContext';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';

export function OrgSwitcher({ collapsed }: { collapsed?: boolean }) {
  const { t, lang, dir } = useI18n();
  const { user, org, organizations, switchOrg } = useAuth();
  const navigate = useNavigate();
  const [newOrgOpen, setNewOrgOpen] = useState(false);

  /**
   * Which company you are in must never be a guess.
   *
   * This used to fall back to the literal "Railit" — which is a REAL organization
   * here (the platform's own workspace), so whenever /auth/me was slow or failed
   * the chrome confidently named the wrong tenant. The chain is: the loaded org,
   * then the slug carried inside the access token itself (real data, present
   * before any request resolves), then a skeleton. Never a made-up name.
   */
  const orgName = (lang === 'ar' ? org?.name_ar : null) ?? org?.name ?? user?.org_slug ?? '';

  /** Display names shared by more than one company, so we can disambiguate. */
  const duplicateNames = useMemo(() => {
    const seen = new Map<string, number>();
    for (const o of organizations) {
      const n = ((lang === 'ar' ? o.name_ar : null) ?? o.name) || '';
      seen.set(n, (seen.get(n) ?? 0) + 1);
    }
    return new Set([...seen].filter(([, n]) => n > 1).map(([name]) => name));
  }, [organizations, lang]);

  if (collapsed) {
    // In the icon rail there is no room for a name. The tile alone still opens
    // the list, so switching never requires expanding the sidebar first.
    return (
      <>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              title={orgName}
              aria-label={t('your_companies')}
              className="flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-foreground"
            >
              <Building2 className="size-4" />
            </button>
          </DropdownMenuTrigger>
          <OrgList
            align={dir === 'rtl' ? 'end' : 'start'}
            {...{ organizations, org, switchOrg, duplicateNames, lang, t, navigate, setNewOrgOpen }}
          />
        </DropdownMenu>
        <NewOrgDialog open={newOrgOpen} onOpenChange={setNewOrgOpen} />
      </>
    );
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            data-testid="org-switcher"
            className={cn(
              'flex min-w-0 flex-1 items-center gap-1.5 rounded-md border border-transparent px-2 py-1.5 text-start transition-colors',
              'hover:border-border hover:bg-sidebar-accent',
            )}
          >
            {orgName ? (
              <span className="min-w-0 flex-1 truncate text-[0.8125rem] font-medium">
                {orgName}
              </span>
            ) : (
              <Skeleton className="h-4 w-24 flex-1" />
            )}
            <ChevronsUpDown className="size-3.5 shrink-0 text-muted-foreground/60" />
          </button>
        </DropdownMenuTrigger>
        <OrgList
          align={dir === 'rtl' ? 'end' : 'start'}
          {...{ organizations, org, switchOrg, duplicateNames, lang, t, navigate, setNewOrgOpen }}
        />
      </DropdownMenu>
      <NewOrgDialog open={newOrgOpen} onOpenChange={setNewOrgOpen} />
    </>
  );
}

/** The list itself, so the collapsed and expanded triggers cannot diverge. */
function OrgList({
  align,
  organizations,
  org,
  switchOrg,
  duplicateNames,
  lang,
  t,
  navigate,
  setNewOrgOpen,
}: {
  align: 'start' | 'end';
  /* `days_left` comes from the same summary the auth context holds: null on a
     healthy company, a countdown on one that is being deleted. */
  organizations: {
    id: string;
    name: string;
    name_ar: string | null;
    slug: string;
    role: string;
    days_left?: number | null;
  }[];
  org: { id: string } | null;
  switchOrg: (id: string) => Promise<void>;
  duplicateNames: Set<string>;
  lang: string;
  t: (k: string) => string;
  navigate: (to: string) => void;
  setNewOrgOpen: (v: boolean) => void;
}) {
  return (
    <DropdownMenuContent align={align} className="min-w-64">
      <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
        {t('your_companies')}
      </DropdownMenuLabel>
      {organizations.map((o) => {
        const name = (lang === 'ar' ? o.name_ar : null) ?? o.name;
        return (
          <DropdownMenuItem
            key={o.id}
            onSelect={() => {
              if (o.id !== org?.id) void switchOrg(o.id);
            }}
            className="gap-3"
          >
            <span className="min-w-0 flex-1">
              <span className="block truncate">{name}</span>
              {/* Two companies can share a display name — the slug is what
                  actually tells them apart, so show it when it matters. */}
              {duplicateNames.has(name || '') ? (
                <span className="block truncate font-mono text-[10.5px] text-muted-foreground">
                  {o.slug}
                </span>
              ) : null}
            </span>
            <span className="flex shrink-0 items-center gap-2">
              {/*
                A COMPANY BEING DELETED SAYS SO, HERE, IN RED.

                The switcher listed it as a normal place to go, next to a
                working company, with nothing but its role beside it. Somebody
                who closed one of their two companies then had two identical
                looking entries and no way to tell which one is counting down
                to being destroyed.

                It stays in the list rather than disappearing, and stays
                clickable: switching into it is how you reach the screen that
                brings it back. What it must not do is look ordinary.
              */}
              {o.days_left !== null && o.days_left !== undefined ? (
                <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-[10.5px] font-medium text-destructive">
                  {t('org_switch_deleting').replace('{n}', String(o.days_left))}
                </span>
              ) : (
                <span className="text-xs text-muted-foreground">{o.role}</span>
              )}
              {o.id === org?.id ? <Check className="size-3.5 text-brand-ink" /> : null}
            </span>
          </DropdownMenuItem>
        );
      })}
      <DropdownMenuSeparator />
      <DropdownMenuItem onSelect={() => setNewOrgOpen(true)}>
        <Plus className="size-4" />
        {t('new_company')}
      </DropdownMenuItem>
      <DropdownMenuItem onSelect={() => navigate('/settings/organization')}>
        <SettingsIcon className="size-4" />
        {t('settings_organization')}
      </DropdownMenuItem>
    </DropdownMenuContent>
  );
}
